import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueryClient } from '@tanstack/react-query';

import { useUpdateGroupDevices } from '../api/mutations';
import { realtimeListQuery } from '../api/queries';

type Notify = (severity: string, message: string) => void;

interface PoolDevice {
    deviceId: string;
    name: string;
    companyId: string | null;
    groupIds: string[];
}

const fromApiRecord = (record: any): PoolDevice => ({
    deviceId: record.device_id,
    name: record.name,
    companyId: record.company_id ?? null,
    groupIds: record.group_ids || [],
});

/**
 * The manage-devices dialog for a custom group. Its device pool is the ACTIVE
 * devices only (archived devices can't be group members).
 */
function useGroupMembers({
    groupId,
    cards,
    archived,
    notify,
}: {
    groupId: string;
    cards: any[];
    archived: boolean;
    notify: Notify;
}) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime' });
    const queryClient = useQueryClient();
    const updateGroupDevices = useUpdateGroupDevices();
    const [open, setOpen] = useState(false);
    const [devices, setDevices] = useState<PoolDevice[]>([]);

    // Opens at once, seeded with the cards on screen when that's the Active tab, then
    // refines from the active list. That list usually comes from the cache (the page
    // polls it), so this rarely costs a request; a failed fetch never blocks the dialog.
    const openDialog = async () => {
        setDevices(
            archived
                ? []
                : cards.map((card) => ({
                      deviceId: card.deviceId,
                      name: card.name,
                      companyId: card.companyId,
                      groupIds: card.groupIds,
                  }))
        );
        setOpen(true);

        try {
            const records = await queryClient.fetchQuery(realtimeListQuery(false));
            const pool = new Map<string, PoolDevice>();
            records.forEach((record) => {
                if (record?.device_id && !pool.has(record.device_id)) pool.set(record.device_id, fromApiRecord(record));
            });
            // Keep the order already shown, so the list doesn't reshuffle when the
            // fresh data lands: refresh listed devices in place, append new ones by id,
            // drop ones no longer active (the fetched pool is authoritative).
            setDevices((prev) => {
                const seen = new Set<string>();
                const kept: PoolDevice[] = [];
                prev.forEach((device) => {
                    const fresh = pool.get(device.deviceId);
                    if (fresh) {
                        kept.push(fresh);
                        seen.add(device.deviceId);
                    }
                });
                const appended = [...pool.values()]
                    .filter((device) => !seen.has(device.deviceId))
                    .sort((a, b) => String(a.deviceId).localeCompare(String(b.deviceId)));
                return [...kept, ...appended];
            });
        } catch (error) {
            console.error('Failed to load device pool for group management:', error);
        }
    };

    // Resolves false on failure, which keeps the dialog open for a retry.
    const confirm = async ({ add, remove }: { add: string[]; remove: string[] }) => {
        try {
            const results = await updateGroupDevices.mutateAsync({ groupId, add, remove });
            // The API skips devices it won't move (e.g. another company's device on a
            // vendor account). Say so instead of silently showing no change.
            const skipped = results.filter((r: any) => r?.result === 'skipped').length;
            if (skipped > 0) notify('warning', t('Groups.devicesSkipped', { count: skipped }));
            return true;
        } catch {
            notify('error', t('Groups.membersFailed'));
            return false;
        }
    };

    return {
        open,
        close: () => setOpen(false),
        devices,
        memberIds: devices.filter((device) => device.groupIds?.includes(groupId)).map((device) => device.deviceId),
        openDialog,
        confirm,
    };
}

export default useGroupMembers;
